// N7-4 — THE ONE DEFINITION OF WHAT A MEMBER'S CREATION IS CALLED.
//
// THE BUG THIS EXISTS TO MAKE IMPOSSIBLE. The shelf title for a made thing was built by GLUING the
// member's own name onto the generated document's title:
//
//     title: `${name || "You"} · ${r.report?.title || "Conjure Oil"}`   // -> "Alexandra · How to Spell Your Oil"
//
// and `shelfTitle` (src/rooms.js) then reformats any "<subject> · <reading>" into "<reading>, <first
// name>" — so the shelf read **"How to Spell Your Oil, Alexandra"**. That is a document title wearing a
// member's name, which reads as though Lala named the oil after her. She never named it anything.
//
// THE LAW: a thing a member MAKES is called what THEY called it, VERBATIM, standing alone — or, when
// they did not name it, an honest label for the kind of thing it is, also standing alone. The two are
// NEVER welded together, and a generated document title NEVER masquerades as a name the member chose.
//
// Shared by the client (the name fields, the shelf) and the server (the saved title), because a title
// law with two copies is a law with two answer keys.

// The honest label per kind of made thing. Used when, and only when, the member did not name it.
export const CREATION_LABELS = {
  "oils-working": "Conjure Oil",
  "spell": "Conjure Spell",
  "cauldron-working": "Cauldron Working",
  "note": "My words",
};

export function creationLabel(type) {
  return CREATION_LABELS[String(type || "").trim()] || "Your working";
}

// A title has to fit on one shelf row. This is the only reason a member's name is touched at all.
export const MAX_CREATION_NAME = 80;

// The member's own name for a thing, VERBATIM. Line breaks and tabs become a single space (a title is
// one line), the ends are trimmed, and it is capped. Nothing else: no capitalization, no spell-check,
// no collapsing of their spacing. Blank means they did not name it.
export function cleanCreationName(input) {
  return String(input ?? "").replace(/[\r\n\t]+/g, " ").trim().slice(0, MAX_CREATION_NAME);
}

// THE TITLE. Their name alone, or the honest label alone. Note there is deliberately no branch that
// can produce a " · " — that separator is what `shelfTitle` reformats, and reformatting a member's own
// words is the whole defect.
export function creationTitle(type, memberGivenName) {
  return cleanCreationName(memberGivenName) || creationLabel(type);
}

// True when the title on a shelf row is a name the member chose rather than one we composed. Used to
// pre-fill the rename box with their own words instead of making them retype them.
export function isMemberNamed(type, title) {
  const t = cleanCreationName(title);
  return !!t && t !== creationLabel(type);
}
